import { SeoLocale, SeoMetadata } from "../../http/protocol";
import { SeoField, SeoMetadataByLocaleType } from "../seo/seoTypes";
import { entriesInServedLocales, hasInvalidOgImageUrl, SeoDraftStore, seoByLocale, SeoSaveError, withSeoField } from "./seoDraftStore";

/** The part of a single target's SEO context the draft reads: the locales it may send. */
export interface SingleTargetSeoContextType {
  locales?: SeoLocale[];
}

/** A single target's SEO editor response: its stored entries and the context the editor shows them in. */
export interface SingleTargetSeoEditorType<TContext extends SingleTargetSeoContextType> {
  context?: TContext;
  entries?: SeoMetadata[];
}

/** The service calls a single target's draft loads and saves through. */
export interface SingleTargetSeoServiceType<TContext extends SingleTargetSeoContextType> {
  getSeo(targetId: number): Promise<SingleTargetSeoEditorType<TContext>>;
  saveSeo(targetId: number, request: { entries?: SeoMetadata[] }): Promise<SeoMetadata[]>;
}

/**
 * The SEO values being edited for one target with a single set of locales, such as a casino category
 * or game. They are only sent after the target itself is saved, so the SEO save never goes out for a
 * target whose own save failed.
 */
export class SingleTargetSeoDraft<TContext extends SingleTargetSeoContextType> extends SeoDraftStore<SingleTargetSeoEditorType<TContext>> {
  private context?: TContext;
  private values: SeoMetadataByLocaleType = {};

  constructor(private readonly service: SingleTargetSeoServiceType<TContext>) {
    super();
  }

  protected fetch(targetId: number): Promise<SingleTargetSeoEditorType<TContext>> {
    return this.service.getSeo(targetId);
  }

  protected applyLoaded(editor: SingleTargetSeoEditorType<TContext>): void {
    this.context = editor.context;
    this.values = seoByLocale(editor.entries);
  }

  getContext(): TContext | undefined {
    return this.context;
  }

  getValues(): SeoMetadataByLocaleType {
    return this.values;
  }

  setField(locale: string, field: SeoField, value: string): void {
    this.values = withSeoField(this.values, locale, field, value);
    this.notify();
  }

  /**
   * Fetches the target's SEO again and takes only its context, so values derived from the stored target
   * are current while the operator's drafts, saved or not, stay as they are.
   */
  protected async reloadContext(targetId?: number): Promise<void> {
    if (targetId === undefined || targetId !== this.getLoadedId()) {
      return;
    }
    const editor = await this.fetch(targetId);
    if (targetId === this.getLoadedId()) {
      this.context = editor.context;
      this.notify();
    }
  }

  /**
   * Sends the drafts for the target the backend just stored. Drafts loaded for another target, or never
   * loaded at all, as for a target saved for the first time, are not sent. The drafts stay in place
   * when the request fails, so the next save of the target retries them.
   */
  protected async saveAfterTargetSave(targetId?: number): Promise<void> {
    if (targetId === undefined || targetId !== this.getLoadedId()) {
      return;
    }
    const entries = entriesInServedLocales(this.values, this.context?.locales);
    if (entries.length === 0) {
      return;
    }
    if (hasInvalidOgImageUrl(entries)) {
      throw new SeoSaveError("invalid-og-image-url");
    }
    const saved = await this.whileSaving(() => this.service.saveSeo(targetId, { entries }));
    this.values = seoByLocale(saved);
    this.notify();
  }
}
